import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { ReimbursementAttributes } from '../../../interfaces/reimbursment';
import InputPresentational from '../../Form/InputPresentational';
import AdminBaseReimbursementFormModal from './AdminBaseReimbursementFormModal';
import { useReimbursementForm } from './hooks/useReimbursementForm';
import { simplifiedReimbursementSchema } from './validation';

interface AdminSimplifiedReimbursementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ReimbursementAttributes, files: File[]) => Promise<void>;
  initialData?: Partial<ReimbursementAttributes>;
  isEdit?: boolean;
  simplifiedFor: 'lesson' | 'course' | 'event';
}

const AdminSimplifiedReimbursementFormModal: React.FC<
  AdminSimplifiedReimbursementFormModalProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEdit = false,
  simplifiedFor,
}) => {
  const {
    register,
    errors,
    handleSubmit,
    watch,
    setValue,
    control,
    isSubmitting,
    files,
    handleFileChange,
    handleDrop,
    handleRemovePending,
  } = useReimbursementForm({
    initialData,
    onSubmit,
    onClose,
    resolver: yupResolver(simplifiedReimbursementSchema),
    simplifiedFor,
  });

  return (
    <AdminBaseReimbursementFormModal
      control={control}
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEdit
          ? `Modifica rendicontazione ${simplifiedFor === 'event' ? 'evento' : simplifiedFor === 'course' ? 'corso' : 'lezione'}`
          : `Nuova rendicontazione ${simplifiedFor === 'event' ? 'evento' : simplifiedFor === 'course' ? 'corso' : 'lezione'}`
      }
      isEdit={isEdit}
      register={register}
      errors={errors}
      handleSubmit={handleSubmit}
      watch={watch}
      setValue={setValue}
      isSubmitting={isSubmitting}
      files={files}
      handleFileChange={handleFileChange}
      handleDrop={handleDrop}
      handleRemovePending={handleRemovePending}
    >
      <div className='bg-white p-6 rounded-lg space-y-4'>
        <h4 className='text-lg font-medium text-gray-900'>
          Descrizione Libera
        </h4>
        <div>
          <textarea
            id='description'
            rows={4}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent'
            placeholder='Inserisci una descrizione per questa rendicontazione'
            {...register('module.description')}
          ></textarea>
          {errors.module?.description && (
            <p className='mt-1 text-sm text-red-600'>
              {errors.module?.description.message}
            </p>
          )}
        </div>
      </div>

      {/* Payment Information */}
      <div className='bg-white p-6 rounded-lg space-y-4'>
        <h4 className='text-lg font-medium text-gray-900'>Dati Pagamento</h4>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <InputPresentational
            id='totalCompensation'
            label='Importo compenso *'
            name='totalCompensation'
            type='text'
            register={register}
            error={errors.totalCompensation}
          />
          {(simplifiedFor === 'lesson' || simplifiedFor === 'event') && (
            <InputPresentational
              id='totalReimbursement'
              label='Importo rimborso *'
              name='totalReimbursement'
              type='text'
              register={register}
              error={errors.totalReimbursement}
            />
          )}
          <div>
            <InputPresentational
              id='module.simplifiedPaymentDate'
              label='Data pagamento'
              name='module.simplifiedPaymentDate'
              type='date'
              register={register}
              error={errors.module?.simplifiedPaymentDate}
            />
            <span className='text-xs text-gray-500'>
              (Lasciare vuoto se non ancora pagato)
            </span>
          </div>
        </div>
      </div>
    </AdminBaseReimbursementFormModal>
  );
};

export default AdminSimplifiedReimbursementFormModal;
